import React, { useState, useEffect } from 'react';
import JWModal from '../brand/JWModal';
import JWInput from '../brand/JWInput';
import {
    AddButtonIcon,
    MessageSVG as MessageIcon,
    MarketSVG as MarketIcon
} from '../icons';
import SearchIconSVG from '../icons/SearchIconSVG';
import axiosService from '../../utils/axios';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';
import { SkillType } from '../../types/profile';
import { MinusCircle } from '../icons';
import { Button } from 'reactstrap';
import JWButton from '../brand/JWButton';
import {
    openToValues,
    seekingValues,
    offeringValues,
} from '../Profile/Headline/headlineValues';
import './headline-modal.css';
import '../../pages/Onboarding/onboarding-chips.css';
import SearchIconLarge from '../../assets/icons/SearchIconLarge';
import BackArrowIcon from '../../assets/icons/BackArrowIcon';

interface AddHeadlineModalProps {
    show: boolean
    onClose: () => void
    setShow: (show: boolean) => void
    onSave: (headline: string) => Promise<void>
}

interface HeadlineValuesProps {
    values: string[]
    selectedSkill?: string
    setSelectedSkill: (skill: string) => void
    selectedTitle?: string | null
}

function HeadlineValues(props: HeadlineValuesProps) {
    const { values, selectedSkill, setSelectedSkill, selectedTitle } = props;

    // Set the maxHeight based on the selectedTitle
    const maxHeight = selectedTitle === 'Seeking' || selectedTitle === 'Offering'
        ? '300px'  // Fixed height for Seeking and Offering
        : '300px';  // Fixed height for Open To

    return (
        <div
            className="my-2 overflow-y-auto"
            style={{
                height: maxHeight,
                maxHeight,
            }}
        >
            {values.map((value: string) => (
                <Button
                    key={value}
                    active={selectedSkill === value}
                    outline={selectedSkill !== value}
                    className="headline-choice m-1"
                    onClick={() => setSelectedSkill(value)}
                >
                    {value}
                </Button>
            ))}
        </div>
    );
}

const AddHeadlineModal = ({ show, onClose, setShow, onSave }: AddHeadlineModalProps) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [selectedTitle, setSelectedTitle] = useState<string | null>();
    const [selectedSkill, setSelectedSkill] = useState<string>();
    const [currentValues, setCurrentValues] = useState<string[]>([]);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [headline, setHeadline] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleTitleSelection = (title: string, values: string[]) => {
        setSelectedTitle(title);
        setCurrentValues(values);
        setSelectedSkill('');
        setCurrentStep(2);
    };

    const goBack = () => {
        setCurrentStep(1);
        setSelectedSkill('');
    };

    const renderStep1 = () => {
        return (
            <div className="m-2">
                <div className="flex mb-6">
                    <h3 className="text-lg font-semibold">Pick a headline type to proceed</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
                    <button
                        className={`headline-choice p-3 md:p-4 rounded-lg transition-colors flex flex-col items-center justify-center border-2 ${
                            selectedTitle === 'Open To'
                                ? 'bg-purple-600 text-gray-800 border-purple-700'
                                : 'bg-white text-gray-800 border-gray-300 hover:border-purple-500'
                        }`}
                        onClick={() => handleTitleSelection('Open To', openToValues)}
                    >
                        <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 rounded-full bg-opacity-20 bg-purple-200">
                            <div style={{ transform: 'scale(0.9)' }}>
                                <MessageIcon color={selectedTitle === 'Open To' ? '#5D23BB' : '#5D23BB'} />
                            </div>
                        </div>
                        <span className="font-medium text-sm md:text-base">Open To</span>
                        <span className="text-xs mt-1 text-center">Indicate your general aims</span>
                    </button>

                    <button
                        className={`headline-choice p-3 md:p-4 rounded-lg transition-colors flex flex-col items-center justify-center border-2 ${
                            selectedTitle === 'Seeking'
                                ? 'bg-purple-600 text-gray-800 border-purple-700'
                                : 'bg-white text-gray-800 border-gray-300 hover:border-purple-500'
                        }`}
                        onClick={() => handleTitleSelection('Seeking', seekingValues)}
                    >
                        <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 rounded-full bg-opacity-20 bg-purple-200">
                            <SearchIconLarge
                                stroke={selectedTitle === 'Seeking' ? '#5D23BB' : '#5D23BB'}
                            />
                        </div>
                        <span className="font-medium text-sm md:text-base">Seeking</span>
                        <span className="text-xs mt-1 text-center">Find specific skills or collaborators</span>
                    </button>

                    <button
                        className={`headline-choice p-3 md:p-4 rounded-lg transition-colors flex flex-col items-center justify-center border-2 ${
                            selectedTitle === 'Offering'
                                ? 'bg-purple-600 text-gray-800 border-purple-700'
                                : 'bg-white text-gray-800 border-gray-300 hover:border-purple-500'
                        }`}
                        onClick={() => handleTitleSelection('Offering', offeringValues)}
                    >
                        <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 rounded-full bg-opacity-20 bg-purple-200">
                            <div style={{ transform: 'scale(0.85)' }}>
                                <MarketIcon color={selectedTitle === 'Offering' ? '#5D23BB' : '#5D23BB'} />
                            </div>
                        </div>
                        <span className="font-medium text-sm md:text-base">Offering</span>
                        <span className="text-xs mt-1 text-center">Highlight a service you provide</span>
                    </button>
                </div>
            </div>
        );
    };

    const renderStep2 = () => {
        return (
            <div className="m-2">
                <div className="flex items-center mb-6">
                    <button
                        onClick={goBack}
                        className="step-back mr-4 inline-flex rounded-full p-2 text-gray-500 transition-colors"
                        aria-label="Go back"
                    >
                        <BackArrowIcon />
                    </button>
                    <h3 className="text-lg font-semibold">
                        Select your {selectedTitle === 'Open To' ? 'interest' : selectedTitle === 'Seeking' ? 'need' : 'service'}
                    </h3>
                </div>

                <div className="mb-4 p-3 bg-purple-50 rounded-lg flex items-center">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                        {selectedTitle === 'Open To' &&
                            <div style={{ transform: 'scale(0.9)' }}>
                                <MessageIcon color="#5D23BB" />
                            </div>
                        }
                        {selectedTitle === 'Seeking' &&
                            <SearchIconLarge
                                stroke="#5D23BB"
                            />
                        }
                        {selectedTitle === 'Offering' &&
                            <div style={{ transform: 'scale(0.85)' }}>
                                <MarketIcon color="#5D23BB" />
                            </div>
                        }
                    </div>
                    <div>
                        <span className="font-medium text-sm text-purple-800">
                            {selectedSkill ?
                                `${selectedTitle} ${selectedTitle === 'Offering' ? `${selectedSkill} Services` : selectedSkill}` :
                                selectedTitle
                            }
                        </span>
                    </div>
                </div>

                <HeadlineValues
                    values={currentValues}
                    selectedSkill={selectedSkill}
                    setSelectedSkill={setSelectedSkill}
                    selectedTitle={selectedTitle}
                />
            </div>
        );
    };

    return (
        <JWModal
            title={'Choose a headline to boost your signal'}
            show={show}
            onClose={onClose}
            setShow={setShow}
            wrapperClassName="md:w-[800px] w-full md:max-h-[90vh] h-auto"
        >
            <div className="flex flex-col h-full">
                {currentStep === 1 ? renderStep1() : renderStep2()}

                {currentStep === 2 && selectedSkill && (
                    <div className="flex justify-end mt-auto p-4 border-t">
                        <JWButton
                            onClick={async () => {
                                const headlineToSave = selectedTitle === 'Offering' ? `${selectedSkill} Services` : selectedSkill;
                                await onSave(`${selectedTitle  } ${  headlineToSave}`);
                            }}
                        >
                            Save Headline
                        </JWButton>
                    </div>
                )}
            </div>
        </JWModal>
    );
};

export default AddHeadlineModal;
